import { useState } from "react";
import { Box, HStack, Text, Spinner } from "@chakra-ui/react";
import { toaster } from "@/components/ui/toaster";
import { extractBusinessErrorMessage } from "@/services/apiErrorMessage";
import { emptyCampaign } from "@/services/campaignMapper";
import StepBasics from "./steps/StepBasics";
import StepLeadSource from "./steps/StepLeadSource";
import StepCompanyKnowledge from "./steps/StepCompanyKnowledge";
import StepProducts from "./steps/StepProducts";
import StepSalesEnablement from "./steps/StepSalesEnablement";
import StepQualifying from "./steps/StepQualifying";
import StepConversion from "./steps/StepConversion";
import StepStatusAutomation from "./steps/StepStatusAutomation";
import StepChannels from "./steps/StepChannels";
import StepFollowUp from "./steps/StepFollowUp";
import StepOutput from "./steps/StepOutput";
import StepTest from "./steps/StepTest";
import StepConversationStyle from "./steps/StepConversationStyle";

const STEPS = [
    { id: 1, title: "Kampania", short: "Kampania" },
    { id: 2, title: "Źródło leadów", short: "Źródło" },
    { id: 3, title: "Wiedza", short: "Wiedza" },
    { id: 4, title: "Produkty i usługi", short: "Produkty" },
    { id: 5, title: "Sprzedaż", short: "Sprzedaż" },
    { id: 6, title: "Kwalifikacja", short: "Kwalifikacja" },
    { id: 7, title: "Konwersja", short: "Konwersja" },
    { id: 8, title: "Automatyzacja statusów", short: "Statusy" },
    { id: 9, title: "Kanały komunikacji", short: "Kanały" },
    { id: 10, title: "Follow-up", short: "Follow-up" },
    { id: 11, title: "Styl rozmowy", short: "Styl" },
    { id: 12, title: "Output / integracje", short: "Output" },
    { id: 13, title: "Test", short: "Test" },
];

const CampaignFormModal = ({ onClose, initialValues, onSubmit, agentName, scenarioId }) => {
    const [currentStep, setCurrentStep] = useState(1);
    const [values, setValues] = useState(initialValues ?? emptyCampaign());
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isDraftSubmitting, setIsDraftSubmitting] = useState(false);
    const totalSteps = STEPS.length;
    const activeStep = STEPS[currentStep - 1];
    const isEdit = !!initialValues?.id;

    const save = async (status, setPending) => {
        if (!values.name?.trim()) {
            toaster.create({ type: "error", title: "Nazwa kampanii jest wymagana" });
            setCurrentStep(1);
            return;
        }
        setPending(true);
        try {
            await onSubmit?.({ ...values, status });
        } catch (error) {
            toaster.create({
                type: "error",
                title: extractBusinessErrorMessage(error) ?? "Nie udało się zapisać kampanii",
            });
        } finally {
            setPending(false);
        }
    };

    const handleSaveDraft = () => save("Draft", setIsDraftSubmitting);
    const handleActivate = () => save("Active", setIsSubmitting);

    return (
        <Box position="fixed" inset={0} zIndex={50} display="flex" alignItems="stretch" bg="rgba(0,0,0,0.85)">
            <Box
                position="relative"
                display="flex"
                w="full"
                m={{ base: 0, md: 4 }}
                borderRadius={{ base: 0, md: 16 }}
                overflow="hidden"
                bg="bg.primary"
                maxH={{ base: '100vh', md: 'calc(100vh - 2rem)' }}
            >
                {/* Desktop sidebar */}
                <Box
                    flexShrink={0}
                    display={{ base: 'none', md: 'flex' }}
                    flexDirection="column"
                    py={6}
                    overflowY="auto"
                    w="240px"
                    bg="bg.secondary"
                    borderRight="1px solid"
                    borderColor="border.primary"
                >
                    <Box px={5} mb={6}>
                        <Text color="white" fontWeight={600} fontSize="sm" mb={1}>{agentName ?? "SalesManager AI"}</Text>
                        <Text fontSize="xs" color="text.grey">{isEdit ? "Edycja kampanii" : "Nowa kampania"}</Text>
                    </Box>
                    <Box as="nav" display="flex" flexDirection="column" gap={1} px={3}>
                        {STEPS.map((step) => {
                            const isActive = currentStep === step.id;
                            const isDone = currentStep > step.id;
                            return (
                                <Box
                                    key={step.id}
                                    as="button"
                                    type="button"
                                    onClick={() => setCurrentStep(step.id)}
                                    w="full"
                                    display="flex"
                                    alignItems="center"
                                    gap={3}
                                    px={3}
                                    py={2.5}
                                    borderRadius={12}
                                    textAlign="left"
                                    bg={isActive ? "bg.highlight" : "transparent"}
                                    border="1px solid"
                                    borderColor={isActive ? "border.secondary" : "transparent"}
                                >
                                    <Box
                                        flexShrink={0}
                                        w={6}
                                        h={6}
                                        borderRadius="full"
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        fontSize="xs"
                                        fontWeight={700}
                                        bg={isDone ? "green.800" : isActive ? "purple.500" : "bg.highlight"}
                                        color={isDone ? "green.400" : isActive ? "white" : "text.grey"}
                                    >
                                        {isDone ? "✓" : step.id}
                                    </Box>
                                    <Text fontSize="sm" fontWeight={500} color={isActive ? "white" : "text.grey"}>
                                        {step.short}
                                    </Text>
                                </Box>
                            );
                        })}
                    </Box>
                </Box>

                {/* Main content */}
                <Box display="flex" flexDirection="column" flex={1} minW={0}>
                    {/* Mobile step bar */}
                    <Box display={{ base: 'flex', md: 'none' }} px={3} py={3} bg="bg.secondary" borderBottom="1px solid" borderColor="border.primary">
                        <HStack justify="space-between" w="full" gap={1}>
                            {STEPS.map((step) => (
                                <Box
                                    key={step.id}
                                    as="button"
                                    type="button"
                                    onClick={() => setCurrentStep(step.id)}
                                    display="flex"
                                    flexDirection="column"
                                    alignItems="center"
                                    gap={1}
                                    flex={1}
                                >
                                    <Box
                                        w={8} h={8} borderRadius="full" display="flex" alignItems="center" justifyContent="center"
                                        fontSize="xs" fontWeight={700}
                                        bg={currentStep === step.id ? "purple.500" : "bg.highlight"}
                                        color={currentStep === step.id ? "white" : "text.grey"}
                                    >
                                        {step.id}
                                    </Box>
                                    <Text fontSize="9px" color={currentStep === step.id ? "white" : "text.grey"} textAlign="center">{step.short}</Text>
                                </Box>
                            ))}
                        </HStack>
                    </Box>

                    {/* Header */}
                    <Box flexShrink={0} display="flex" alignItems="center" justifyContent="space-between" px={{ base: 4, md: 6 }} py={4} bg="bg.primary" borderBottom="1px solid" borderColor="border.primary">
                        <Box minW={0}>
                            <Text color="white" fontSize={{ base: 'sm', md: 'lg' }} fontWeight={700}>{activeStep.title}</Text>
                            <Text fontSize="xs" color="text.grey" mt={0.5}>Krok {currentStep} z {totalSteps}</Text>
                        </Box>
                        <Box
                            as="button" type="button" onClick={onClose}
                            w={8} h={8} borderRadius={8} display="flex" alignItems="center" justifyContent="center"
                            fontSize="lg" color="text.grey" _hover={{ bg: "bg.highlight" }}
                        >×</Box>
                    </Box>

                    {/* Step content */}
                    <Box flex={1} overflowY="auto" px={{ base: 4, md: 6 }} py={{ base: 4, md: 6 }}>
                        {currentStep === 1 && <StepBasics values={values} onChange={setValues} />}
                        {currentStep === 2 && <StepLeadSource values={values} onChange={setValues} />}
                        {currentStep === 3 && <StepCompanyKnowledge values={values} onChange={setValues} />}
                        {currentStep === 4 && <StepProducts values={values} onChange={setValues} />}
                        {currentStep === 5 && <StepSalesEnablement values={values} onChange={setValues} />}
                        {currentStep === 6 && <StepQualifying values={values} onChange={setValues} campaignId={values.id} scenarioId={scenarioId} />}
                        {currentStep === 7 && <StepConversion values={values} onChange={setValues} />}
                        {currentStep === 8 && <StepStatusAutomation values={values} onChange={setValues} />}
                        {currentStep === 9 && <StepChannels values={values} onChange={setValues} />}
                        {currentStep === 10 && <StepFollowUp values={values} onChange={setValues} />}
                        {currentStep === 11 && <StepConversationStyle values={values} onChange={setValues} />}
                        {currentStep === 12 && <StepOutput values={values} onChange={setValues} />}
                        {currentStep === 13 && <StepTest values={values} />}
                    </Box>

                    {/* Footer */}
                    <Box flexShrink={0} display="flex" alignItems="center" justifyContent="space-between" px={{ base: 4, md: 6 }} py={4} bg="bg.primary" borderTop="1px solid" borderColor="border.primary" gap={2}>
                        <Box
                            as="button" type="button"
                            onClick={() => setCurrentStep(s => Math.max(1, s - 1))}
                            disabled={currentStep === 1}
                            px={4} py={2} borderRadius={8} fontSize="sm"
                            bg="bg.highlight" color="text.white" border="1px solid" borderColor="border.secondary"
                            opacity={currentStep === 1 ? 0.4 : 1}
                            cursor={currentStep === 1 ? "not-allowed" : "pointer"}
                        >
                            Wstecz
                        </Box>
                        <HStack gap={2}>
                            <Box
                                as="button" type="button"
                                onClick={handleSaveDraft}
                                disabled={isDraftSubmitting || isSubmitting}
                                px={4} py={2} borderRadius={8} fontSize="sm"
                                bg="bg.highlight" color="text.white" border="1px solid" borderColor="border.secondary"
                                opacity={isDraftSubmitting || isSubmitting ? 0.6 : 1}
                                display="flex" alignItems="center" gap={2}
                            >
                                {isDraftSubmitting && <Spinner size="xs" />}
                                Zapisz jako szkic
                            </Box>
                            {currentStep < totalSteps ? (
                                <Box
                                    as="button" type="button"
                                    onClick={() => setCurrentStep(s => Math.min(totalSteps, s + 1))}
                                    px={4} py={2} borderRadius={8} fontSize="sm"
                                    bg="purple.500" color="white"
                                >
                                    Dalej
                                </Box>
                            ) : (
                                <Box
                                    as="button" type="button"
                                    onClick={handleActivate}
                                    disabled={isSubmitting || isDraftSubmitting}
                                    px={4} py={2} borderRadius={8} fontSize="sm"
                                    bg="purple.500" color="white"
                                    opacity={isSubmitting || isDraftSubmitting ? 0.6 : 1}
                                    display="flex" alignItems="center" gap={2}
                                >
                                    {isSubmitting && <Spinner size="xs" />}
                                    Zapisz i aktywuj
                                </Box>
                            )}
                        </HStack>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default CampaignFormModal;
